
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CertificatePrintPreview } from "../CertificatePrintPreview";
import { HologramNumberEditor } from "../HologramNumberEditor";
import { ArtworkInformation } from "./ArtworkInformation";
import { PrintInformation } from "./PrintInformation";
import { HologramDialogProps } from "./types";
import { getPreviewUrl } from "@/utils/imageUtils";

export function HologramDialog({
  open,
  onOpenChange,
  certificate,
}: HologramDialogProps) {
  const [currentHologramNumber, setCurrentHologramNumber] = useState(certificate?.hologram_number || "");
  const [showCertificatePreview, setShowCertificatePreview] = useState(false);

  useEffect(() => {
    if (certificate?.hologram_number) {
      setCurrentHologramNumber(certificate.hologram_number);
    }
  }, [certificate]);

  if (!certificate) return null;

  const print = certificate.prints[0];
  const artwork = print.editions.artworks;
  const edition = print.editions;
  const creator = artwork.creators;

  const handlePrintCertificate = () => {
    console.log('Opening certificate preview for print:', print);
    console.log('Certificate data:', {
      id: certificate.certificate_id,
      hologramNumber: currentHologramNumber,
      issueDate: certificate.issue_date
    });
    
    setShowCertificatePreview(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="text-xl">Certificate Details</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-4">
              <ArtworkInformation
                title={artwork.title}
                artist={creator.nick_name || `${creator.name} ${creator.surname}`}
                creationDate={artwork.creation_date}
                technique={artwork.art_technique}
                edition={edition.edition_name}
                dimensions={`${edition.width} x ${edition.height} cm`}
              />
              
              <HologramNumberEditor
                certificateId={certificate.certificate_id}
                currentHologramNumber={currentHologramNumber}
                onUpdate={setCurrentHologramNumber}
              />
            </div>

            <div className="flex flex-col h-full">
              <div className="flex-grow">
                <PrintInformation
                  printNumber={print.edition_number ? 
                    `#${print.edition_number}${edition.edition_limit ? `/${edition.edition_limit}` : ''}`
                    : "N/A"}
                  status="Printed"
                  media={print.media?.media_name}
                  printProvider={print.print_service_providers?.name}
                  hologramNumber={currentHologramNumber}
                />
              </div>

              <Button onClick={handlePrintCertificate} className="w-full mt-auto">
                Print Certificate
              </Button>
            </div>

            {artwork.preview && (
              <div className="relative rounded-lg overflow-hidden flex items-center justify-center bg-muted h-full">
                <img
                  src={getPreviewUrl(artwork.preview)}
                  alt={artwork.title}
                  className="max-w-full max-h-[600px] object-contain"
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {print && (
        <CertificatePrintPreview
          print={{
            ...print,
            status: 'printed',
            certified: true,
            certificate_id: certificate.certificate_id,
            certificates: {
              hologram_number: currentHologramNumber
            },
            issue_date: certificate.issue_date,
            media: print.media,
            print_service_providers: print.print_service_providers,
            editions: {
              ...print.editions,
              artworks: {
                ...print.editions.artworks,
                creation_date: artwork.creation_date,
                art_technique: artwork.art_technique
              }
            }
          }}
          certificate={{
            hologram_number: currentHologramNumber,
            issue_date: certificate.issue_date
          }}
          open={showCertificatePreview}
          onOpenChange={setShowCertificatePreview}
        />
      )}
    </>
  );
}
